import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { getApplyJobStatusDataAtom } from './application-details.context';
import { useAtomValue } from 'jotai';

export const useApplicationTimelineStateList = () => {
  const jaData = useAtomValue(getApplyJobStatusDataAtom);

  const { t } = useTranslation();
  return useMemo(() => {
    const base: IApplicationTimelineState[] = [
      {
        id: 'application-submitted',
        statusKey: ['worker_jp_interested'],
        backendKey: { mainKey: ['worker_jp_interested'], dateKey: 'inserted_at' },
        headText: { mainLabel: t('common:application_received'), subLabel: '15 feb 2024' },
        date: { dateTo: '15/02/2024' },
        bodyText: { mainLabel: t('job-application-page:application_received_body') },
        footerText: {
          mainLabel: t('job-application-page:tips_for_presenting_yourself'),
          href: 'https://www.restworld.it/blog/mestiere/i-nostri-consigli-per-presentarsi-al-meglio',
        },
      },
      {
        // The interview with Serena. It has no `statusKey` of its own on purpose: the
        // application stays `worker_jp_interested` for its whole duration, so this step is
        // driven by the assessment, not by the application status. `useCurrentApplicationStatus`
        // is what makes it the current step while it is pending, and the step is dropped from
        // the timeline altogether when the application has no interview.
        id: 'interview',
        statusKey: [],
        backendKey: { mainKey: [], dateKey: 'inserted_at' },
        // No date in the header, in any state: the badge under the title already says where the
        // interview stands, and there is no countdown anywhere in this flow.
        headText: { mainLabel: t('job-application-page:interview_step_title'), subLabel: '' },
        date: { dateTo: '' },
        // Static in all three states, like every other step: only the badge, the date and the
        // action at the bottom change, and those are rendered by the step itself.
        bodyText: { mainLabel: t('job-application-page:interview_step_body') },
      },
      {
        id: 'application-submitted-to-the-company',
        statusKey: ['worker_jp_in_shortlist', 'worker_jp_viewed_by_employer', 'worker_jp_employer_interested'],
        backendKey: {
          mainKey: ['worker_jp_in_shortlist', 'worker_jp_viewed_by_employer', 'worker_jp_employer_interested'],
          dateKey: 'inserted_at',
        },
        headText: { mainLabel: t('job-application-page:application_sent_to_the_company'), subLabel: '15 feb 2024' },
        date: { dateTo: '15/02/2024' },
        bodyText: { mainLabel: t('job-application-page:application_sent_to_the_company_body') },
        footerText: {
          mainLabel: t('job-application-page:how_to_behave_when_the_company_calls_you'),
          href: 'https://www.restworld.it/blog/mestiere/prima-chiamata-con-azienda',
        },
      },
      {
        id: 'application-under-evaluation',
        statusKey: ['worker_jp_interview_scheduled', 'worker_jp_interview_done'],
        backendKey: {
          mainKey: ['worker_jp_interview_scheduled', 'worker_jp_interview_done'],
          dateKey: 'interview_date',
        },
        headText: {
          mainLabel: t('job-application-page:planned_interview'),
          subLabel: t('job-application-page:show_up_on_feb22'),
        },
        date: { time: '09.15', dateTo: '22/02/2024' },
        bodyText: {
          mainLabel: t('job-application-page:may_the_force_be_with_you'),
          subLabel: `${t('job-application-page:show_up_to_the_interview_and_make_sparks')}🚀`,
          infoLabel: t('job-application-page:cant_remember_to_notify'),
        },
        footerText: {
          mainLabel: t('job-application-page:how_to_behave_at_the_interview'),
          href: 'https://www.restworld.it/blog/mestiere/colloquio-in-ristorazione',
        },
      },
      {
        id: 'trial-period', // stage period
        statusKey: ['worker_jp_stage_scheduled', 'worker_jp_stage', 'worker_jp_stage_done'],
        backendKey: {
          mainKey: ['worker_jp_stage_scheduled', 'worker_jp_stage', 'worker_jp_stage_done'],
          dateKey: 'stage_start_date',
        },
        headText: {
          mainLabel: t('common:trial_period'),
          subLabel: t('job-application-page:from12_mar_to12_apr2024'),
        },
        date: { dateTo: '12/03/2024', dateFrom: '12/04/2024' },
        bodyText: {
          mainLabel: t('job-application-page:wax_on_off'),
          subLabel: t('job-application-page:give_your_best_learn_lessons'),
        },
        footerText: {
          mainLabel: t('job-application-page:preparing_for_a_practice_test'),
          href: 'https://www.restworld.it/blog/mestiere/gestione-del-periodo-di-prova',
        },
      },
      {
        id: 'hired',
        statusKey: ['worker_jp_hired', 'worker_jp_scheduled_hiring'],
        backendKey: {
          mainKey: ['worker_jp_hired', 'worker_jp_scheduled_hiring'],
          dateKey: 'hiring_date',
        },
        headText: {
          mainLabel: t('common:hired'),
          subLabel: '20 Apr 2024 🚀',
        },
        date: { dateTo: '12/03/2024' },
        bodyText: { mainLabel: t('job-application-page:tips_for_getting_off') },
        footerText: {
          mainLabel: t('job-application-page:how_to_overcome_first_day_anxiety'),
          href: 'https://www.restworld.it/blog/mestiere/come-superare-lansia-del-primo-giorno-di-lavoro',
        },
      },
      {
        id: 'spontaneous-application',
        statusKey: [],
        backendKey: { mainKey: [], dateKey: 'inserted_at' },
        headText: {
          mainLabel: t('job-application-page:spontaneous_application_sent'),
          subLabel: '15 feb 2024',
        },
        date: { dateTo: '12/03/2024' },
        bodyText: { mainLabel: t('job-application-page:contact_you_to_get_to_know_you_better') },
        footerText: {
          mainLabel: t('job-application-page:how_to_behave_when_the_company_calls_you'),
          href: 'https://www.restworld.it/blog/mestiere/prima-chiamata-con-azienda',
        },
      },
      {
        id: 'rejected',
        statusKey: ['worker_jp_discarded'],
        backendKey: { mainKey: ['worker_jp_discarded'], dateKey: 'inserted_at' },
        headText: {
          mainLabel:
            jaData?.discarded_by === 'worker'
              ? t('job-application-page:withdrawn_application')
              : t('job-application-page:unsuitable_application'),
          subLabel: '22 feb 2024',
        },
        date: { dateTo: '12/03/2024' },
        bodyText: { mainLabel: t('job-application-page:you_have_been_rejected_by_the_employer') },
        footerText: {
          mainLabel: t('job-application-page:what_to_do_if_a_selection_doesnt_go_through'),
          href: 'https://www.restworld.it/blog/mestiere/come-cercare-lavoro-ristorazione-senza-farti-fregare',
        },
      },
      {
        id: 'not-interested',
        statusKey: ['worker_jp_not_interested'],
        backendKey: { mainKey: ['worker_jp_not_interested'], dateKey: 'inserted_at' },
        headText: {
          mainLabel: t('common:not_interested'),
          subLabel: '22 feb 2024',
        },
        date: { dateTo: '12/03/2024' },
        bodyText: { mainLabel: t('job-application-page:you_can_always_change_your_mind') },
      },
    ];
    const filtered = jaData?.job_position?.published_from_ats
      ? base.filter((s) => !['application-under-evaluation', 'trial-period', 'hired'].includes(s.id))
      : base;
    return filtered;
  }, [t, jaData?.job_position?.published_from_ats, jaData?.discarded_by]) as Partial<IApplicationTimelineState[]>;
};

export const timelineIdList = [
  'application-submitted',
  'interview',
  'application-submitted-to-the-company',
  'application-under-evaluation',
  'trial-period',
  'hired',
  'spontaneous-application',
  'rejected',
  'not-interested',
] as const;
export const timelineIdMapping = {
  'application-submitted': ['worker_jp_interested'],
  'application-submitted-to-the-company': [
    'worker_jp_in_shortlist',
    'worker_jp_viewed_by_employer',
    'worker_jp_employer_interested',
  ],
  'application-under-evaluation': ['worker_jp_interview_scheduled', 'worker_jp_interview_done'],
  'trial-period': ['worker_jp_stage_scheduled', 'worker_jp_stage', 'worker_jp_stage_done'],
  hired: ['worker_jp_hired', 'worker_jp_scheduled_hiring'],
  rejected: ['worker_jp_discarded'],
  'not-interested': ['worker_jp_not_interested'],
};
export type TimeLineKeysType =
  | 'application-submitted'
  | 'interview'
  | 'application-submitted-to-the-company'
  | 'application-under-evaluation'
  | 'trial-period'
  | 'hired'
  | 'spontaneous-application'
  | 'rejected'
  | 'not-interested';
export type ApplicationStateType = 'rejected' | 'accepted' | 'pending' | 'not-found' | 'not-interested';
export const pendingStatusKey = [
  'worker_jp_interested',
  'worker_jp_applied',
  'worker_jp_in_shortlist',
  'worker_jp_viewed_by_employer',
  'worker_jp_employer_interested',
  'worker_jp_interview_scheduled',
  'worker_jp_interview_done',
  'worker_jp_stage_scheduled',
  'worker_jp_stage',
  'worker_jp_stage_done',
];
export const hiredStatusKeys = ['worker_jp_scheduled_hiring', 'worker_jp_hired'];
export const rejectedStatusKeys = ['worker_jp_discarded'];
export const notInterestedStatusKeys = ['worker_jp_not_interested'];

export interface IApplicationTimelineState {
  id: TimeLineKeysType;
  statusKey?: string[];
  backendKey: IBackendKey;
  headText: IHeadTextOrBodyText;
  date: IDate;
  bodyText: IBodyText;
  footerText?: IFooterText;
}
export interface IBackendKey {
  mainKey?: string[];
  dateKey: string;
}
export interface IHeadTextOrBodyText {
  mainLabel: string;
  subLabel: string;
}
export interface IDate {
  dateTo: string;
  time?: string;
  dateFrom?: string;
}
export interface IBodyText {
  mainLabel: string;
  subLabel?: string;
  infoLabel?: string;
}
export interface IFooterText {
  mainLabel: string;
  href: string;
}
